import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { ProductCartListSortCol, TProductCartList, TProductCartListResponse, TProductCartListQuery, TProductCartListSortCol, adaptProductCartFromListResponse, adaptProductCartListQueryToRequest } from '../../types';
import { BACKEND_PRODUCT_CART } from '../../backend-paths';


export const productCartListGetApi = createAsyncThunk(
	BACKEND_PRODUCT_CART,
	(payload: TProductCartListQuery | undefined, thunkApi) => {
		const params = adaptProductCartListQueryToRequest(payload);

		return HttpService.get<TProductCartListResponse>(BACKEND_PRODUCT_CART, { params }).then(result => {
			return adaptProductCartFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProductCartList;
	query: TProductCartListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: ProductCartListSortCol.PROVIDER,
	},
	error: undefined,
};

export const productCartListSlice = createSlice({
	name: 'productCartListSlice',
	initialState,
	reducers: {
		setProductCartListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setProductCartListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setProductCartListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setProductCartListQueryUserNickname(state, action: PayloadAction<string>) {
			const userNickname = action.payload || undefined;
			const isUserNickname = userNickname && userNickname.length > 0;

			state.query.userNickname = isUserNickname ? userNickname : '';
		},

		setProductCartListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setProductCartListQuerySortCol(state, action: PayloadAction<TProductCartListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearProductCartList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[productCartListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[productCartListGetApi.fulfilled.type]: (state, action: PayloadAction<TProductCartList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[productCartListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useProductCartListSelector = () => useAppSelector(state => state[productCartListSlice.name]);

export const { clearProductCartList, setProductCartListQueryLimit, setProductCartListQueryOffset, setProductCartListQuerySort, setProductCartListQuerySortCol, setProductCartListQuerySearch, setProductCartListQueryUserNickname } = productCartListSlice.actions;
